import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users,
  Clock,
  DollarSign,
  Shield,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Briefcase,
  FileText,
  Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CPAServices = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: 'Cost Reduction up to 60% Guaranteed',
      description:
        'Reduce your overhead costs significantly by leveraging our skilled offshore team without compromising on quality.',
    },
    {
      icon: Clock,
      title: 'Scale On-Demand',
      description:
        'Ramp up during tax season and scale down during off-peak times. Pay only for what you need.',
    },
    {
      icon: Users,
      title: 'Experienced Professionals',
      description:
        'Access qualified accountants, CPAs, and bookkeepers who understand Canadian tax laws and standards.',
    },
    {
      icon: Shield,
      title: 'Data Security & Confidentiality',
      description:
        'Bank-level security protocols, NDAs, and strict confidentiality agreements protect your client data.',
    },
    {
      icon: TrendingUp,
      title: 'Focus on Growth',
      description:
        'Delegate routine work so your in-house team can focus on client relationships and higher-value advisory services.',
    },
    {
      icon: Zap,
      title: 'Fast Turnaround',
      description:
        'Leverage time zone advantages for overnight work delivery. Get more done in less time.',
    },
  ];

  const services = [
    {
      title: 'Tax Preparation Support',
      items: [
        'T1 Personal Tax Returns',
        'T2 Corporate Tax Returns',
        'GST/HST Returns',
        'Payroll Tax Filings',
        'Cross-border tax returns',
      ],
    },
    {
      title: 'Bookkeeping & Accounting',
      items: [
        'Full-cycle bookkeeping',
        'Bank & credit card reconciliations',
        'AR/AP management',
        'Month-end close support',
        'Year-end adjustments',
        'QuickBooks / Xero / Sage support',
      ],
    },
    {
      title: 'Assurance & Compilation',
      items: [
        'Compilation engagements (NTR)',
        'Review engagement support',
        'Audit working papers preparation',
        'Financial statement drafting',
        'Working paper documentation',
        'Lead schedules preparation',
      ],
    },
    {
      title: 'Advisory Support',
      items: [
        'Financial analysis & reporting',
        'Cash flow forecasting',
        'Budgeting & projections',
        'KPI dashboards',
        'Management reports',
        'Business valuations support',
      ],
    },
  ];

  const workflow = [
    {
      step: '01',
      title: 'Discovery Call',
      description:
        "We understand your firm's needs, workflow, and pain points to design a custom solution.",
    },
    {
      step: '02',
      title: 'Team Selection',
      description:
        'We match you with qualified professionals experienced in your specific software and workflows.',
    },
    {
      step: '03',
      title: 'Secure Onboarding',
      description:
        'Sign NDAs, set up secure access, and establish communication protocols with your dedicated team.',
    },
    {
      step: '04',
      title: 'Seamless Delivery',
      description:
        'Your outsourced team works as an extension of your firm, delivering high-quality work on time.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-br from-[#005782] via-[#01435c] to-[#003a52] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20">
                <Briefcase className="w-4 h-4" />
                <span className="text-sm font-semibold uppercase tracking-wider">
                  For CPA and Accounting Firms
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
                CPA Outsourcing &<br />
                <span className="text-yellow-300">Manpower Services</span>
              </h1>
              <p className="text-xl md:text-2xl leading-relaxed text-white/90 mb-8">
                Scale your CPA firm with our experienced offshore accounting professionals. Reliable, secure, and cost-effective outsourcing solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button
                    data-testid="cpa-hero-cta-btn"
                    className="bg-white text-[#005782] hover:bg-slate-100 rounded-lg px-8 py-4 text-lg font-medium transition-all shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    Get a Free Consultation
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Button
                  asChild
                  className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-[#005782] rounded-lg px-8 py-4 text-lg font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <a href="tel:+14377570478">Call +1 (437) 757-0478</a>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-yellow-300 mb-2">60%</div>
                <div className="text-white/90">Cost Reduction Guaranteed</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-yellow-300 mb-2">24/7</div>
                <div className="text-white/90">Support Available</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-yellow-300 mb-2">100%</div>
                <div className="text-white/90">Data Security</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-2xl font-bold text-yellow-300 mb-2">Experienced</div>
                <div className="text-white/90">Accountants</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm text-[#005782] uppercase tracking-widest font-semibold">
              Why Choose Us
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mt-4 mb-4">
              Benefits for Your CPA Firm
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Partner with us to unlock efficiency, quality, and growth for your practice
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow hover:border-[#005782]/20"
                >
                  <div className="w-14 h-14 bg-[#E0F2FE] rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#005782]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm text-[#005782] uppercase tracking-widest font-semibold">
              What We Offer
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mt-4 mb-4">
              CPA Outsourcing Services
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              End-to-end back-office support tailored for Canadian CPA firms
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#005782] rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F172A]">
                    {service.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm text-[#005782] uppercase tracking-widest font-semibold">
              How It Works
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mt-4 mb-4">
              Simple 4-Step Process
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Get started with our outsourcing services in less than a week
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workflow.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-[#E0F2FE] to-white rounded-2xl p-8 h-full border border-slate-100">
                  <div className="text-5xl font-bold text-[#005782]/20 mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#005782] to-[#01435c] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Scale Your CPA Firm?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Let's discuss how our outsourcing services can help you reduce costs, improve efficiency, and grow your practice. Book a free consultation today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cpa-cta-btn"
                className="bg-white text-[#005782] hover:bg-slate-100 rounded-lg px-8 py-4 text-lg font-medium shadow-lg transition-all whitespace-nowrap"
              >
                Book Free Consultation
              </Button>
            </Link>
            <Button
              asChild
              className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-[#005782] rounded-lg px-8 py-4 text-lg font-medium transition-all whitespace-nowrap"
            >
              <a href="mailto:info@vktax.ca">Email info@vktax.ca</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CPAServices;
